import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'
import { sileo } from 'sileo'
import LucaModal from '../../../../../ui/LucaModal'
import { Button, Select } from '../../../../../ui/index'
import { BankAPI } from '../../../../../../../api/bancos.api'
import { CLP_CURRENCY_ID, USD_CURRENCY_ID } from './creditCard.utils'

const bankAPI = new BankAPI()

const normalizeBankName = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const CURRENCY_OPTIONS = [
  { value: CLP_CURRENCY_ID, label: 'CLP' },
  { value: USD_CURRENCY_ID, label: 'USD' },
  { value: 'eur', label: 'EUR — próximamente', disabled: true }
]

const ModalCreateCreditCard = ({
  isOpen,
  onClose,
  businessId,
  onCreated,
  zIndex,
  initialBankId = '',
  initialBankName = '',
  initialLast4Digits = '',
  initialCurrencyId = CLP_CURRENCY_ID,
}) => {
  const [bankId, setBankId] = useState('')
  const [last4Digits, setLast4Digits] = useState('')
  const [currencyId, setCurrencyId] = useState(CLP_CURRENCY_ID)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setBankId(initialBankId ? String(initialBankId) : '')
    setLast4Digits(String(initialLast4Digits || '').replace(/\D/g, '').slice(-4))
    setCurrencyId(initialCurrencyId)
  }, [isOpen, initialBankId, initialLast4Digits, initialCurrencyId])

  const banksQuery = useQuery({
    queryKey: ['credit-card-select-banks', businessId],
    queryFn: async () => {
      const response = await bankAPI.getBanks(businessId)
      return response.data
    },
    enabled: Boolean(businessId) && isOpen
  })

  useEffect(() => {
    if (!isOpen || !initialBankName) return
    const target = normalizeBankName(initialBankName)
    const match = (banksQuery.data || []).find(bank => {
      const name = normalizeBankName(bank.name)
      return name === target || name.includes(target) || target.includes(name)
    })
    if (match) setBankId(String(match.id))
  }, [isOpen, initialBankName, banksQuery.data])

  const createCreditCardMutation = useMutation({
    mutationFn: () => bankAPI.createCreditCard(businessId, {
      bankId: Number(bankId),
      last4Digits,
      currencyId
    }),
    onSuccess: response => {
      const created = response.data
      sileo.success({
        title: 'Tarjeta agregada',
        description: <span className='text-green-500/80 font-medium'>La tarjeta quedó disponible para seleccionar.</span>
      })
      resetState()
      onCreated(created)
    },
    onError: mutationError => {
      setError(mutationError.response?.data?.message || 'No se pudo agregar la tarjeta.')
    }
  })

  const resetState = () => {
    setBankId('')
    setLast4Digits('')
    setCurrencyId(CLP_CURRENCY_ID)
    setError(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSubmit = () => {
    if (!bankId) {
      setError('falta completar el campo: banco')
      return
    }
    if (!/^\d{4}$/.test(last4Digits)) {
      setError('falta completar el campo: últimos 4 dígitos')
      return
    }
    setError(null)
    createCreditCardMutation.mutate()
  }

  const banks = banksQuery.data || []
  const bankOptions = [
    { value: '', label: 'Selecciona un banco' },
    ...banks.map(bank => ({ value: String(bank.id), label: bank.name }))
  ]

  return (
    <LucaModal
      open={isOpen}
      onClose={handleClose}
      maxWidth='max-w-xl'
      zIndex={zIndex}
      footer={
        <>
          <Button
            title='Cancelar'
            onClick={handleClose}
            className='text-[10px] bg-primary text-white rounded-full'
          />
          <Button
            title={createCreditCardMutation.isPending ? 'Guardando...' : 'Guardar tarjeta'}
            onClick={handleSubmit}
            disabled={createCreditCardMutation.isPending}
            className='text-[10px] bg-accent2 text-white rounded-full'
          />
        </>
      }
    >
      <div className='flex-1 overflow-y-auto p-6'>
        <div className='flex items-center gap-3'>
          <div className='h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0'>
            <CreditCard size={20} />
          </div>
          <div>
            <h3 className='text-lg font-bold text-slate-900'>Agregar tarjeta de crédito</h3>
            <p className='text-xs text-slate-500'>
              Registra la tarjeta para poder asociarle movimientos y cartolas.
            </p>
          </div>
        </div>

        <div className='mt-6 space-y-4'>
          <div>
            <label className='block text-xs font-semibold text-slate-600 mb-1'>Banco</label>
            <Select
              value={bankId}
              onChange={event => setBankId(event.target.value)}
              options={bankOptions}
              className='w-full border border-gray-300 px-3 py-2 pr-9 rounded-full shadow-sm text-sm font-sans bg-white'
            />
          </div>

          <div>
            <label className='block text-xs font-semibold text-slate-600 mb-1'>Últimos 4 dígitos</label>
            <input
              type='text'
              inputMode='numeric'
              maxLength={4}
              value={last4Digits}
              onChange={event => setLast4Digits(event.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder='1234'
              className='w-full border border-gray-300 px-3 py-2 rounded-full shadow-sm text-sm font-sans'
            />
          </div>

          <div>
            <label className='block text-xs font-semibold text-slate-600 mb-1'>Moneda</label>
            <Select
              value={currencyId}
              onChange={event => setCurrencyId(Number(event.target.value))}
              options={CURRENCY_OPTIONS}
              className='w-full border border-gray-300 px-3 py-2 pr-9 rounded-full shadow-sm text-sm font-sans bg-white'
            />
            <p className='mt-1 text-[11px] text-slate-400'>
              {currencyId === USD_CURRENCY_ID
                ? 'Los movimientos se convertirán a CLP con el valor del dólar del día de cada movimiento.'
                : 'Próximamente podrás hacerlo con euros.'}
            </p>
          </div>

          {error && (
            <div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              {error}
            </div>
          )}
        </div>
      </div>
    </LucaModal>
  )
}

export default ModalCreateCreditCard
